/**
 * EnterpriseContactModal - Enterprise inquiry form
 * 
 * Captures: company info, sites, employees, platform preference
 * Sends to support@standardsprotocol.com
 */
import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { X, Building, Send, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import './EnterpriseContactModal.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EnterpriseContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    contact_name: '',
    email: '',
    company_name: '',
    company_size: '',
    num_sites: '',
    employees_per_site: '',
    platform_preference: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res = await fetch(`${API}/contact/enterprise`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to submit inquiry');
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Submit error:', err);
      toast.error(err.message || 'Failed to submit inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      contact_name: '',
      email: '',
      company_name: '',
      company_size: '',
      num_sites: '',
      employees_per_site: '',
      platform_preference: '',
      message: ''
    });
    setSubmitted(false);
    onClose();
  };

  // Success state
  if (submitted) {
    return (
      <div className="enterprise-modal-overlay" onClick={handleClose}>
        <div className="enterprise-modal success" onClick={e => e.stopPropagation()}>
          <button className="enterprise-modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
          
          <div className="success-content">
            <div className="success-icon">
              <CheckCircle size={48} />
            </div>
            <h2>Thank You</h2>
            <p>We've received your inquiry and will be in touch within 24 hours.</p>
            <p className="success-note">A confirmation email has been sent to your inbox.</p>
            <Button onClick={handleClose}>Close</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="enterprise-modal-overlay" onClick={handleClose}>
      <div className="enterprise-modal" onClick={e => e.stopPropagation()}>
        <button className="enterprise-modal-close" onClick={handleClose}>
          <X size={20} />
        </button>

        <div className="enterprise-modal-header">
          <Building size={28} />
          <h2>Enterprise Inquiry</h2>
          <p>Tailored solutions for your organisation</p>
        </div>

        <form onSubmit={handleSubmit} className="enterprise-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contact_name">Your Name *</label>
              <input
                type="text"
                id="contact_name"
                name="contact_name"
                value={formData.contact_name}
                onChange={handleChange}
                required
                placeholder="John Smith"
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john@company.com"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="company_name">Company Name *</label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                required
                placeholder="Acme Industries"
              />
            </div>
            <div className="form-group">
              <label htmlFor="company_size">Company Size *</label>
              <select
                id="company_size"
                name="company_size"
                value={formData.company_size}
                onChange={handleChange}
                required
              >
                <option value="">Select...</option>
                <option value="1-10">1-10 employees</option>
                <option value="11-50">11-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="200+">200+ employees</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="num_sites">Number of Sites *</label>
              <select
                id="num_sites"
                name="num_sites"
                value={formData.num_sites}
                onChange={handleChange}
                required
              >
                <option value="">Select...</option>
                <option value="1">1 site</option>
                <option value="2-5">2-5 sites</option>
                <option value="6-10">6-10 sites</option>
                <option value="10+">10+ sites</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="employees_per_site">Employees per Site *</label>
              <select
                id="employees_per_site"
                name="employees_per_site"
                value={formData.employees_per_site}
                onChange={handleChange}
                required
              >
                <option value="">Select...</option>
                <option value="1-10">1-10 per site</option>
                <option value="11-50">11-50 per site</option>
                <option value="51-100">51-100 per site</option>
                <option value="100+">100+ per site</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="platform_preference">How would a platform best support you? *</label>
            <select
              id="platform_preference"
              name="platform_preference"
              value={formData.platform_preference}
              onChange={handleChange}
              required
            >
              <option value="">Select...</option>
              <option value="site_specific">Site-specific for employees</option>
              <option value="linked_management">Front-line managers linked to senior management</option>
              <option value="single_platform">Single platform for all</option>
              <option value="not_sure">Not sure yet</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="message">Additional Information</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your specific requirements..."
              rows={3}
            />
          </div>

          <div className="form-actions">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <><Loader2 className="animate-spin" size={16} /> Submitting...</>
              ) : (
                <><Send size={16} /> Submit Inquiry</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnterpriseContactModal;
